import esriReverseGeocode from '@rsos/maps/providers/esri/locator/reverseGeocode';
import type { GISAddressCandidate } from './helpers';

// Number of milliseconds when the local GIS reverse geocoding request should timeout.
const REQUEST_TIMEOUT_MS = 3000;

interface EsriReverseGeocodeArgs {
  serviceUrl: string;
  latitude: number;
  longitude: number;
  requestOptions?: { signal?: AbortSignal };
  token?: string | null;
}

// The Esri provider is an untyped JS module whose JSDoc makes TS infer a
// positional signature; cast it to the object-arg shape it actually accepts.
const reverseGeocode = esriReverseGeocode as unknown as (
  args: EsriReverseGeocodeArgs,
) => Promise<{ attributes?: GISAddressCandidate } | undefined>;

export interface GISReverseGeocodeLocationArgs {
  latitude: number;
  longitude: number;
  onReverseGeocoding?: (isReverseGeocoding: boolean) => void;
  serviceUrl?: string | null;
  token?: string | null;
}

/**
 * Reverse geocode coordinates using the specified geocode service url. The service returns an
 * address candidate if one is found, otherwise null; on request failure it throws so callers can
 * fall back to the next service.
 */
const gisReverseGeocodeLocation = async ({
  latitude,
  longitude,
  onReverseGeocoding,
  serviceUrl,
  token,
}: GISReverseGeocodeLocationArgs): Promise<GISAddressCandidate | null> => {
  if (!serviceUrl) throw new Error('serviceUrl is required.');

  if (onReverseGeocoding) onReverseGeocoding(true);

  try {
    // When the reverse geocode API request is complete, but the Promise fails to resolve or reject,
    // it remains in the pending state. A requestOptions object with the signal property is passed
    // to the function so that it can be manually aborted. More information on this below:
    // https://developers.arcgis.com/javascript/latest/api-reference/esri-rest-locator.html#locationToAddress
    // https://developers.arcgis.com/javascript/latest/api-reference/esri-request.html#RequestOptions
    // https://developer.mozilla.org/en-US/docs/Web/API/AbortSignal#aborting_a_fetch_operation_with_a_timeout
    const requestOptions = { signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS) };

    const response = await reverseGeocode({
      serviceUrl,
      latitude,
      longitude,
      requestOptions,
      token,
    });

    const addressCandidate = response?.attributes;

    return addressCandidate || null;
  } catch {
    throw new Error();
  } finally {
    if (onReverseGeocoding) onReverseGeocoding(false);
  }
};

export default gisReverseGeocodeLocation;
